// ---------------------------------------------------------------------------
// Shared artifact (CAVE-extracted document) helpers.
//
// Lives in the domain layer so both the review UI (ArtifactSummaryReview /
// CaveUploadReview) and the submit-time validator can share the same required
// field rules without a circular dependency.
// ---------------------------------------------------------------------------

// Required extracted fields per artifact type. DD-214 fields are all required;
// death certificate underlying causes B/C/D are optional per the standard, so
// they are intentionally omitted here.
export const DD214_REQUIRED = [
  'gradeRateRank',
  'payGrade',
  'dateInducted',
  'causeOfSeparation',
  'separationType',
  'separationCode',
  'characterOfService',
];

export const DEATH_CERT_REQUIRED = [
  'decendentDateOfDisposition',
  'causeOfDeath',
  'mannerOfDeath',
  'decendentMaritalStatus',
];

export const entryHasRequiredMissing = (entry, artifactType) => {
  const required =
    artifactType === 'dd214' ? DD214_REQUIRED : DEATH_CERT_REQUIRED;
  return required.some(f => !entry[f]);
};

export const pageHasErrors = (files = []) =>
  files.some(file => {
    const artifacts = file?.idpArtifacts;
    if (!artifacts) return false;
    return (
      (artifacts.dd214 ?? []).some(e => entryHasRequiredMissing(e, 'dd214')) ||
      (artifacts.deathCertificates ?? []).some(e =>
        entryHasRequiredMissing(e, 'deathCertificates'),
      )
    );
  });

// Form-data key of the hidden anchor field the validation error is mapped to.
export const CAVE_VALIDATION_FIELD = 'view:caveArtifacts';

/**
 * Submit-time validator for forms-system `customValidationErrors`.
 *
 * The extracted document data (DD-214 / death certificate) is reviewed inline on
 * the review-and-submit page but has no dedicated form page, so it can't be
 * validated through normal page schemas. This validator runs at submit time and
 * blocks submission when any processed document is missing required fields. The
 * error is mapped to the hidden `view:caveArtifacts` anchor field so it surfaces
 * on the correct chapter (see pages/uploadDocumentsPage and CAVE_VALIDATION_FIELD).
 *
 * No-op unless CAVE is enabled (`view:idpEnabled`), so non-CAVE submissions are
 * never blocked.
 *
 * @param {Object} formData - full form data
 * @param {Object} [options]
 * @param {string} [options.filesKey='files'] - key holding the uploaded files array
 * @param {string} [options.toggleKey='view:idpEnabled'] - form-data key for the CAVE toggle
 * @returns {Array} jsonschema-shaped error objects (empty when complete)
 */
export const getCaveArtifactValidationErrors = (
  formData,
  { filesKey = 'files', toggleKey = 'view:idpEnabled' } = {},
) => {
  if (formData?.[toggleKey] !== true) {
    return [];
  }
  const files = formData?.[filesKey] ?? [];
  if (!pageHasErrors(files)) {
    return [];
  }
  return [
    {
      name: 'required',
      property: `instance.${CAVE_VALIDATION_FIELD}`,
      message:
        'Review your uploaded documents and complete the missing information before submitting.',
      stack: `instance.${CAVE_VALIDATION_FIELD} has missing required information`,
    },
  ];
};
